import { withStyles } from "@material-ui/core";
import { useState, useEffect } from 'react';
import DueBill from "./DueBills";
import IBBill from "./IBBill";
import { getDataWithQueryString } from "./../../../../api/componentAPIs";
import { compose } from "recompose";
import { withUserProfile } from "../../../../userProfile/userProfileContext";
import { withSnackbar } from "notistack";
import {
  checkAllBillsSelectedInBatch,
  checkInPlanShipmentsExistInBatch,
  checkPartialBillsSelectedInBatch,
  getExistingShipmentsInPlan,
  getInitialIBData,
} from "./utils";
import { withPlanDate } from "../PlanDateContext";
import { isValid as isDateTimeValid } from "date-fns";
import appConstants from "../../../../appConstants";
import { getIBBillsTabs, getTabDataFromConfig, emitRefreshTimerEvent } from '../utils';
import { SHIPMENT_SELECTION_STEPS } from '../constants';

const styles = (theme) => ({
  button: {
    marginLeft: 16,
  },
  paperAnchor: {
    position: "absolute",
    padding: 0,
    borderLeft: `1px solid ${theme.AppBGColor}`,
    boxShadow: "none",
    width: "100%",
    height: "100%",
    boxSizing: "border-box",
    backgroundColor: theme.gridBGColor,
    transform: "translate(0px, 0px)",
    transition: theme.transitions.create(["width"], {
      easing: theme.transitions.easing.easeIn,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  paperHidden: {
    height: 0,
    width: 0,
  },
  backdrop: {
    position: "absolute",
    backgroundColor: "transparent",
  },
  modal: {
    position: "absolute",
  },
});

const IBBills = (props) => {
  const [tabsData, setTabsData] = useState(null);
  const [openLayover, setOpenLayover] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [ibEtaCutOff, setIbEtaCutOff] = useState(props.ibEtaCutOff);
  const [errorMessage, setErrorMessage] = useState(appConstants.GRID_STATUS.LOADING);
  const [tabId, setTabId] = useState(null);

  useEffect(() => {
    // When updating an existing plan, we navigate directly to the IB Bills step.
    // The ibEtaCutOff value is calculated asynchronously by the GetInitializedPlanDates API,
    // so props.ibEtaCutOff is initially null. This effect updates the local state
    // once the calculated value becomes available.
    if(props.ibEtaCutOff && isDateTimeValid(props.ibEtaCutOff)){
      setIbEtaCutOff(props.ibEtaCutOff);
    }
  }, [props.ibEtaCutOff]);

  useEffect(() => {
    if (isDateTimeValid(ibEtaCutOff)) {
      fetchInboundBillsData();
    }
  }, [ibEtaCutOff]);

  const fetchInboundBillsData = (isRefresh = false, shipmentIdsToSelect = []) => {
    const {
      userData: { userProfile },
      planDate,
    } = props;
    getDataWithQueryString(
      "GetInboundBills",
      `terminalId=${userProfile.terminal.id}&terminalCode=${userProfile.terminal.code}&planDate=${planDate}&ibEtaCutOff=${ibEtaCutOff}`
    ).then(
      (response) => {
        if (response?.status === 200) {
          const selectedShipmentIds = isRefresh ? shipmentIdsToSelect : props.selectedShipmentIds;
          const iBBillsTabConfigJSON = response.data;
          if(!isRefresh){
            // Only change the tab if it is not a refresh
            const activeTab = Object.keys(iBBillsTabConfigJSON).find(
              (key) => iBBillsTabConfigJSON[key]?.metadata?.isActive
            );
            const defaultTab = activeTab || getIBBillsTabs(iBBillsTabConfigJSON)[0]?.id;
            setTabId(defaultTab);
          }
          const updatedTabsData = {};
          Object.keys(iBBillsTabConfigJSON).forEach((tabId) => {
            const tabData = getTabDataFromConfig(iBBillsTabConfigJSON, tabId);
            updatedTabsData[tabId] = {
              data: getInitialIBData({
                IBData: tabData,
                selectedShipmentIds: selectedShipmentIds,
                planningTerminal: userProfile.terminal.code,
                isRefresh: isRefresh
              }),
              metadata: {
                tabName: iBBillsTabConfigJSON[tabId].metadata.tabName
              }
            };
          });
          emitRefreshTimerEvent(SHIPMENT_SELECTION_STEPS.IB_BILLS);
          setTabsData(updatedTabsData);
          setErrorMessage(appConstants.GRID_STATUS.SUCCESS);
        } else {
          handleError();
        }
      },
      (_error) => {
        handleError();
      }
    );
  };

  const handleRefresh = () => {
    // Refresh the data and keep the selected shipments
    const shipmentIdsToSelect = getAllSelectedShipments();
    fetchInboundBillsData(true, shipmentIdsToSelect);
  }

  const handleError = () => {
    props.enqueueSnackbar("Unable to fetch inbound bills", {
      variant: "error",
    });
    setTabsData(null);
    setErrorMessage(appConstants.GRID_STATUS.ERROR);
  };

  const showDueBills = (selectedBatch) => {
    setSelectedBatch(selectedBatch);
    setOpenLayover(true);
  };

  const closeOverlay = () => {
    setOpenLayover(false);
  };

  const updateIBBillSelection = (ibtrailer, checked) => {
    if (checkInPlanShipmentsExistInBatch(ibtrailer.shipmentsForDestTerminal)) { // disabled trailer selection
      return ibtrailer;
    }
    
    const selectedBills = checked ? ibtrailer.shipmentsForDestTerminal.map((bill) => bill.proId) : [];
  
    return {
      ...ibtrailer,
      isSelected: checked,
      selectedPros: selectedBills,
      isIntermediate: false,
    };
  };

  const updateAllIBBills = (checked, filteredTrailers) => {
    const updatedData = tabsData?.[tabId]?.data?.map((batch) => {
      if (filteredTrailers.includes(batch.id)) {
        return updateIBBillSelection(batch, checked);
      }
      return batch;
    });
    updateTabData(updatedData,tabId);
  };

  const updateSelectedIBBills = (selectedTrailer, checked) => {
    const updatedData = tabsData?.[tabId]?.data?.map((trailer) =>
      trailer.id === selectedTrailer.id ? updateIBBillSelection(trailer, checked) : trailer
    );
    updateTabData(updatedData,tabId);
  };

  const updateSelectedBills = (selectedPros) => {
    const updatedData = tabsData?.[tabId]?.data?.map((batch) => {
      if (batch.id === selectedBatch?.id) {
        const inPlanBills = getExistingShipmentsInPlan(
          batch.shipmentsForDestTerminal
        );
        const totalSelectedBills = [...inPlanBills, ...selectedPros];
        return {
          ...batch,
          selectedPros,
          isSelected: checkAllBillsSelectedInBatch(
            totalSelectedBills.length,
            batch.shipmentsForDestTerminal.length
          ),
          isIntermediate: checkPartialBillsSelectedInBatch(
            totalSelectedBills.length,
            batch.shipmentsForDestTerminal.length
          ),
        };
      }
      return batch;
    });    
    updateTabData(updatedData,tabId);
    setOpenLayover(false);
  };

  const updateTabData =(updatedData,tabId)=>{
    const updatedTabsData = { ...tabsData };
    updatedTabsData[tabId].data = updatedData;
    setTabsData(updatedTabsData);

  }

  const getAllSelectedShipments = () => {
    return Object.values(tabsData).reduce((accumulatedShipments, tabInfo) => {
      return [
        ...accumulatedShipments,
        ...tabInfo.data.reduce((accumulatedBatches, shipmentBatch) => {
          return [...accumulatedBatches, ...shipmentBatch.selectedPros];
        }, [])
      ];
    }, []);
  };

  const handleNext = () => {
    const allSelectedShipments = getAllSelectedShipments();
    props.isLastStep
      ? props.handleSubmit(allSelectedShipments, ibEtaCutOff)
      : props.goToNextStep(allSelectedShipments);
  };

  const onIBCutoffChanged = (value) => {
    if (value && value !== ibEtaCutOff && isDateTimeValid(value)) {
      setIbEtaCutOff(value);
      setTabsData(null);
      setErrorMessage(appConstants.GRID_STATUS.LOADING);
    }
  };

  const getTrailerDetails = (selectedBatch) => {
    const {trailerNumber, minIBETA} = selectedBatch;
    return {
      trailerNumber,
      ibeta: minIBETA
    }
  }

  const onTabChange = (event, newTabId) => {
    if (tabId !== newTabId) {
      setTabId(newTabId);
    }
  };

  const {
    classes,
    userData: { userProfile },
    isLastStep,
    isShipmentSelectionCompleted
  } = props;

  return (
    <>
      <IBBill
        data={tabsData?.[tabId]?.data ?? null} //When using [] as fallback value, a flicker is happening in the grid while loading the data
        glEventHub={props.glEventHub}
        showDueBills={showDueBills}
        handleNext={handleNext}
        goBack={props.goBack}
        updateSelectedIBBill={updateSelectedIBBills}
        updateAllIBBills={updateAllIBBills}
        userProfile={userProfile}
        isLastStep={isLastStep}
        onIBCutoffChanged={onIBCutoffChanged}
        ibEtaCutOff={ibEtaCutOff}
        gridErrorMessage={errorMessage}
        isShipmentSelectionCompleted={isShipmentSelectionCompleted}
        tabList={getIBBillsTabs(tabsData)}
        tabId={tabId}
        onTabChange={onTabChange}
        handleRefresh={handleRefresh}
      />
      {openLayover && (
        <DueBill
          data={selectedBatch?.shipmentsForDestTerminal}
          trailerDetails={getTrailerDetails(selectedBatch)}
          selectedPros={selectedBatch?.selectedPros}
          glEventHub={props.glEventHub}
          onSubmit={updateSelectedBills}
          onCancel={closeOverlay}
          serviceLevelList={props.serviceLevelList}
          userProfile={userProfile}
          open
          anchor="bottom"
          variant="persistent"
          drawerClasses={{
            paper: `${classes.paperAnchor} customScroll ${
              !openLayover ? classes.paperHidden : ""
            }`,
            modal: classes.modal,
          }}
          BackdropProps={{
            className: classes.backdrop,
          }}
        />
      )}
    </>
  );
}

export default compose(
  withPlanDate,
  withUserProfile,
  withSnackbar,
  withStyles(styles)
)(IBBills);
